// @ts-nocheck
/**
 * バリエーション生成モジュール
 * ダンジョンの細部（背景、ギミック、時間帯など）を決定
 * @module twinKey/core/dungeon/variationGenerator
 */

import { GIMMICKS } from '../../data/constants';
import { getContinent } from './data/continents';

// ============================================================
// 時間帯定義
// ============================================================

const TIME_OF_DAY = {
  dawn: { id: 'dawn', name: '黎明', icon: '🌅', modifier: { accuracy: 0.9 } },
  day: { id: 'day', name: '昼', icon: '☀️', modifier: {} },
  dusk: { id: 'dusk', name: '黄昏', icon: '🌇', modifier: { evasion: 1.1 } },
  night: { id: 'night', name: '夜', icon: '🌙', modifier: { darkness: 1.2 } },
};

// ============================================================
// 報酬ボーナスタイプ
// ============================================================

const REWARD_BONUS_TYPES = {
  normal: { id: 'normal', name: '通常', multiplier: 1.0, description: '標準報酬' },
  gold_rush: { id: 'gold_rush', name: '黄金郷', multiplier: 1.5, description: 'ゴールド+50%' },
  exp_boost: { id: 'exp_boost', name: '経験の泉', multiplier: 1.0, expBonus: 1.5, description: '経験値+50%' },
  item_bonus: { id: 'item_bonus', name: '宝物庫', multiplier: 1.0, itemRate: 1.3, description: 'アイテムドロップ+30%' },
  rare_spawn: { id: 'rare_spawn', name: '希少出現', multiplier: 1.0, rareRate: 2.0, description: 'レア敵出現率2倍' },
};

// ============================================================
// メイン関数
// ============================================================

/**
 * ダンジョンのバリエーションを生成
 * @param {number} seed - シード値
 * @param {Object} continent - 大陸情報
 * @returns {Object} バリエーション情報
 */
export function generateVariation(seed, continent) {
  const random = seededRandom(seed);

  // 背景決定
  const backgrounds = continent.backgrounds || ['洞窟'];
  const backgroundIndex = Math.floor(random() * backgrounds.length);
  const background = backgrounds[backgroundIndex];

  // 敵の属性傾向
  const enemyBias = selectEnemyBias(continent, random);

  // ギミック決定
  const gimmick = selectGimmick(continent, random);

  // 時間帯決定
  const timeOfDay = selectTimeOfDay(random);

  // 報酬ボーナス決定
  const rewardBonus = selectRewardBonus(random);

  return {
    background,
    enemyBias,
    gimmick,
    timeOfDay,
    rewardBonus,
  };
}

/**
 * 敵の属性傾向を決定
 * @param {Object} continent - 大陸情報
 * @param {Function} random - 乱数関数
 * @returns {string} 属性
 */
function selectEnemyBias(continent, random) {
  const bias = continent.attributeBias || [];

  if (bias.length === 0) {
    // バイアスなしの場合はランダム
    const allAttributes = ['炎', '水', '光', '闇', '原始', '未来'];
    return allAttributes[Math.floor(random() * allAttributes.length)];
  }

  // バイアスがある場合は70%でバイアス内、30%でランダム
  if (random() < 0.7) {
    return bias[Math.floor(random() * bias.length)];
  }

  const allAttributes = ['炎', '水', '光', '闇', '原始', '未来'];
  return allAttributes[Math.floor(random() * allAttributes.length)];
}

/**
 * ギミックを決定
 * @param {Object} continent - 大陸情報
 * @param {Function} random - 乱数関数
 * @returns {Object} ギミック情報
 */
function selectGimmick(continent, random) {
  // 大陸に関連するギミックを優先
  const continentGimmicks = getRelatedGimmicks(continent.id);

  // 50%でギミックなし、30%で大陸関連、20%でランダム
  const roll = random();

  if (roll < 0.5) {
    return GIMMICKS.find(g => g.id === 'none');
  }

  if (roll < 0.8 && continentGimmicks.length > 0) {
    return continentGimmicks[Math.floor(random() * continentGimmicks.length)];
  }

  // ランダムギミック（noneを除く）
  const activeGimmicks = GIMMICKS.filter(g => g.id !== 'none');
  return activeGimmicks[Math.floor(random() * activeGimmicks.length)];
}

/**
 * 大陸に関連するギミックを取得
 * @param {string} continentId - 大陸ID
 * @returns {Array} ギミック配列
 */
function getRelatedGimmicks(continentId) {
  const mapping = {
    aviculus: ['lava', 'ash_storm'],
    black_curse: ['poison_swamp', 'miasma'],
    steam_boundary: ['fog', 'lava'],
    twilight_realm: ['fog', 'miasma'],
    time_rift: ['ash_storm'],
    primal_depths: ['poison_swamp'],
    unknown: [],
  };

  const gimmickIds = mapping[continentId] || [];
  return GIMMICKS.filter(g => gimmickIds.includes(g.id));
}

/**
 * 時間帯を決定
 * @param {Function} random - 乱数関数
 * @returns {Object} 時間帯情報
 */
function selectTimeOfDay(random) {
  const times = Object.values(TIME_OF_DAY);
  const index = Math.floor(random() * times.length);
  return times[index];
}

/**
 * 報酬ボーナスを決定
 * @param {Function} random - 乱数関数
 * @returns {Object} 報酬ボーナス情報
 */
function selectRewardBonus(random) {
  // 70%で通常、30%で特殊ボーナス
  if (random() < 0.7) {
    return REWARD_BONUS_TYPES.normal;
  }

  const specialTypes = Object.values(REWARD_BONUS_TYPES).filter(t => t.id !== 'normal');
  const index = Math.floor(random() * specialTypes.length);
  return specialTypes[index];
}

// ============================================================
// シード付き乱数関数
// ============================================================

/**
 * シード付き乱数生成器を作成
 * mulberry32アルゴリズム
 * @param {number} seed - シード値
 * @returns {Function} 乱数関数
 */
export function seededRandom(seed) {
  let state = seed;
  return function() {
    state |= 0;
    state = (state + 0x6D2B79F5) | 0;
    let t = Math.imul(state ^ (state >>> 15), 1 | state);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/**
 * シード付きでシャッフル
 * @param {Array} array - 配列
 * @param {number} seed - シード値
 * @returns {Array} シャッフル済み配列
 */
export function shuffleWithSeed(array, seed) {
  const random = seededRandom(seed);
  const shuffled = [...array];

  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }

  return shuffled;
}

/**
 * シード付きで配列からランダム選択
 * @param {Array} array - 配列
 * @param {number} seed - シード値
 * @returns {*} 選択された要素
 */
export function pickWithSeed(array, seed) {
  const random = seededRandom(seed);
  return array[Math.floor(random() * array.length)];
}

// ============================================================
// エクスポート
// ============================================================

export {
  TIME_OF_DAY,
  REWARD_BONUS_TYPES,
};
